import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

// Helper function to calculate age in months
const calculateAgeInMonths = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  
  if (today.getDate() < birthDate.getDate()) {
    months--;
  }
  
  if (months < 0) {
    years--;
    months += 12;
  }

  const totalMonths = years * 12 + months;
  return totalMonths < 0 ? 0 : totalMonths;
};

// Helper function to find the best fitting band
const findBestBand = (bands, ageInMonths) => {
  // First, filter out any bands that don't have valid, numeric age ranges.
  const validBands = bands.filter(
    (band) =>
      typeof band.age_min_months === "number" &&
      typeof band.age_max_months === "number"
  );

  // If there are no valid bands, we can't proceed.
  if (validBands.length === 0) {
    console.error("No bands with valid age ranges were found.");
    return null;
  }

  // Find a band where the child's age is perfectly within the range.
  const fittingBand = validBands.find(
    (band) => ageInMonths >= band.age_min_months && ageInMonths <= band.age_max_months
  );
  if (fittingBand) {
    return fittingBand;
  }

  // If no perfect fit is found, find the band with the closest age range.
  // This ensures a band is always returned if there are valid bands available.
  let closestBand = validBands[0];
  let minDiff = Infinity;

  validBands.forEach((band) => {
    const diff = Math.min(
      Math.abs(ageInMonths - band.age_min_months),
      Math.abs(ageInMonths - band.age_max_months)
    );
    if (diff < minDiff) {
      minDiff = diff;
      closestBand = band;
    }
  });

  return closestBand;
};


export default function MilestonesStatus() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const location = useLocation();
  const [child, setChild] = useState(location.state?.updatedChild || null);
  const [band, setBand] = useState(null); // Changed from bands to a single band
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const accessToken = localStorage.getItem('access');
        const config = {
          headers: { Authorization: `Bearer ${accessToken}` },
        };

        let currentChild = child;
        if (!currentChild) {
          const childResponse = await api.get(`/children/profiles/${childId}/`, config);
          currentChild = childResponse.data;
          setChild(currentChild);
        }

        const bandsResponse = await api.get("/children/global-bands/", config);
        const allBands = bandsResponse.data;
        
        console.log("Fetched child:", currentChild);
        console.log("Fetched bands:", allBands);

        if (currentChild && currentChild.dob && allBands && allBands.length > 0) {
            const ageInMonths = calculateAgeInMonths(currentChild.dob);
            console.log("Calculated age in months:", ageInMonths);
            const bestBand = findBestBand(allBands, ageInMonths);
            console.log("Found best band:", bestBand);
            setBand(bestBand);
        } else {
            console.error("Could not set band. Conditions not met:", {
                hasChild: !!currentChild,
                hasDob: !!currentChild?.dob,
                hasBands: !!allBands && allBands.length > 0,
            });
        }

        const responsesResponse = await api.get(`/children/global-responses/?child=${childId}`, config);
        const existingAnswers = responsesResponse.data.reduce((acc, response) => {
          if (response.choices.length > 0) {
            acc[response.question.id] = response.choices[0].option.id;
          }
          return acc;
        }, {});
        setSelectedAnswers(existingAnswers);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load milestone data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [childId, child]);

  const handleAnswerSelect = (questionId, optionId) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: optionId,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    if (!band) return;

    const questionIds = new Set(
      band.sets.flatMap(s => s.questions.map(q => q.id))
    );

    const responses = Object.keys(selectedAnswers)
      .filter(questionId => questionIds.has(parseInt(questionId)))
      .map(questionId => ({
        question: questionId,
        options: [selectedAnswers[questionId]],
      }));

    try {
      if (responses.length > 0) {
        await api.post("/children/global-responses/bulk_create/", {
          child: childId,
          responses,
        });
      }
      navigate("/client-dashboard");
    } catch (error) {
      console.error("Error submitting answers:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-red-500 text-center mt-10">{error}</div>;
  }

  if (!child || !band) {
    return <div className="text-center mt-10">Data not found or no suitable milestone band for this child's age.</div>;
  }

  const handleBack = () => {
   navigate(-1);;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {submitting && <LoadingSpinner />}
      <div className="bg-white lg:shadow-sm">
        <div className="lg:mx-auto lg:max-w-7xl px-5 lg:px-8 pt-6 pb-4 lg:py-4">
          <button onClick={handleBack} className="text-gray-800 hover:text-gray-600 lg:mb-0 mb-6">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1e3a5f] lg:hidden">
            Developmental Milestones<br />
            <span className="text-base font-normal text-gray-600">({band.title})</span>
          </h1>
        </div>
      </div>

      <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:py-8">
        <div className="hidden lg:block mb-8">
          <h1 className="text-3xl font-bold text-[#1e3a5f]">
            Developmental Milestones ({band.title})
          </h1>
          <p className="mt-2 text-gray-600">
            Select the areas where your child is currently excelling or may need support. This helps us personalise insights and suggestions.
          </p>
        </div>

        <div className="bg-white lg:rounded-2xl lg:shadow-sm lg:p-10 px-5 pb-32 lg:pb-0">
          <p className="text-sm text-gray-600 mb-6 pt-4 lg:pt-0 lg:hidden">
            Select the areas where your child is currently excelling or may need support. This helps us personalise insights and suggestions.
          </p>

          <div className="mb-8 lg:mb-10 flex justify-center">
            <div className="w-full max-w-md">
              <img
                src={band.milestone_image}
                alt={band.title}
                className="w-full h-auto rounded-2xl"
                onLoad={() => console.log(`Milestone image loaded for ${band.title}: ${band.milestone_image}`)}
                onError={(e) => console.error(`Error loading milestone image for ${band.title}:`, e.target.src)}
              />
            </div>
          </div>

          {band.sets.map((set, setIndex) => (
            <div key={set.id} className="mb-8 lg:mb-10">
              <h2 className="text-base lg:text-lg font-semibold text-[#1e3a5f] mb-4">
                {setIndex + 1}. {set.title}
              </h2>
              <div className="space-y-4">
                {set.questions.map((question) => (
                  <div key={question.id}>
                    <p className="text-sm lg:text-base text-gray-800 font-normal mb-3">
                      {question.text}
                    </p>
                    <div className="flex flex-wrap gap-2 lg:gap-3">
                      {question.options.map(option => (
                        <button
                          key={option.id}
                          onClick={() => handleAnswerSelect(question.id, option.id)}
                          className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                            selectedAnswers[question.id] === option.id
                              ? "bg-[#1e3a5f] text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="hidden lg:flex justify-end mt-10">
            <button
              onClick={handleSubmit}
              className="px-12 py-4 rounded-xl bg-[#1e3a5f] text-white font-semibold shadow-lg hover:bg-[#152e4a] transition-all hover:shadow-xl"
            >
              Finish
            </button>
          </div>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white px-5 pb-8 pt-4 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <button
          onClick={handleSubmit}
          className="w-full rounded-xl bg-[#1e3a5f] py-4 text-base font-semibold text-white shadow-md transition-transform active:scale-98"
        >
          Finish
        </button>
        <div className="mt-4 flex justify-center">
          <div className="h-1 w-32 rounded-full bg-gray-800" />
        </div>
      </div>

      <div className="hidden lg:block h-16"></div>
    </div>
  );
}
